<template>
  <v-row dense class="ma-0">
    <v-col cols="12" lg="7">
      <applications-graph
        v-if="hideCommissions"
        style="height: 30rem !important"
        condensed
      />
      <commissions-summary-graph
        v-else
        style="height: 30rem !important"
        condensed
      />
    </v-col>

    <v-col cols="12" lg="5">
      <v-fade-transition mode="out-in">
        <template v-if="loadedGoals">
          <v-card
            v-if="percent !== 100"
            :color="goalsAndWebinarsTabIndex === 0 ? 'accent' : null"
          >
            <v-tabs
              v-model="goalsAndWebinarsTabIndex"
              class="transparent-tab-group"
              :color="goalsAndWebinarsTabIndex === 0 ? 'white' : 'primary'"
              :background-color="
                goalsAndWebinarsTabIndex === 0 ? 'accent' : null
              "
            >
              <v-tab class="text-none">
                <v-card-title style="font-weight: 400; font-size: 1.2rem">
                  BackNine Feed
                </v-card-title>
              </v-tab>
              <v-tab class="text-none" data-testid="onboarding-tab">
                <v-card-title style="font-weight: 400; font-size: 1.2rem">
                  <v-badge
                    :content="incompleteTaskCount"
                    :model-value="Boolean(incompleteTaskCount)"
                    floating
                    offset-y="3"
                    color="pink"
                  >
                    Onboarding
                  </v-badge>
                </v-card-title>
              </v-tab>
            </v-tabs>
            <v-tabs-window v-model="goalsAndWebinarsTabIndex" touchless>
              <v-tabs-window-item>
                <back-nine-news height="27rem" in-group />
              </v-tabs-window-item>

              <v-tabs-window-item>
                <site-goals
                  :goals="goals"
                  :percent="percent"
                  in-group
                  height="27rem"
                />
              </v-tabs-window-item>
            </v-tabs-window>
          </v-card>
          <back-nine-news v-else height="30rem" />
        </template>
        <div v-else>
          <v-skeleton-loader type="card" />
        </div>
      </v-fade-transition>
    </v-col>

    <v-col cols="12" lg="12">
      <v-fade-transition mode="out-in">
        <performance-statistics
          v-if="loadedStatistics"
          :statistics="statistics"
        />
        <div v-else>
          <v-skeleton-loader type="card" />
        </div>
      </v-fade-transition>
    </v-col>
    <v-col id="v-step-3" cols="12" lg="4">
      <v-fade-transition mode="out-in">
        <top-cases
          v-if="loadedRecents"
          :cases="cases"
          class="recent-item-list"
        />
        <div v-else>
          <v-skeleton-loader type="card" />
        </div>
      </v-fade-transition>
    </v-col>
    <v-col cols="12" lg="4">
      <v-fade-transition mode="out-in">
        <top-advisors
          v-if="loadedRecents && advisors.length"
          :advisors="advisors"
          class="recent-item-list"
        />
        <recent-tasks
          v-else-if="loadedRecents"
          :tasks="tasks"
          class="recent-item-list"
        />
        <div v-else>
          <v-skeleton-loader type="card" />
        </div>
      </v-fade-transition>
    </v-col>

    <v-col cols="12" lg="4">
      <recent-eapps
        v-if="loadedRecents"
        :e-apps="eApps"
        class="recent-item-list"
      />
      <div v-else>
        <v-skeleton-loader type="card" />
      </div>
    </v-col>
    <welcome-tour v-if="readyForTutorial" />
  </v-row>
</template>

<script setup>
import CommissionsSummaryGraph from "@/components/reports/CommissionsSummaryGraph.vue";
import ApplicationsGraph from "@/components/reports/ApplicationsGraph.vue";
import PerformanceStatistics from "@/components/home/PerformanceStatistics.vue";
import RecentTasks from "@/components/home/RecentTasks.vue";
import TopCases from "@/components/home/TopCases.vue";
import TopAdvisors from "@/components/home/TopAdvisors.vue";
import RecentEapps from "@/components/home/RecentEapps.vue";
import SiteGoals from "@/components/home/SiteGoals.vue";
import BackNineNews from "@/components/home/BackNineNews.vue";
import WelcomeTour from "@/components/WelcomeTour.vue";

import {
  getAdvisorCards,
  getAdvisorGoals,
  getAdvisorRecents
} from "@/api/dashboard.service";
import { parseErrorMessage } from "@/util/helpers";
import { storeToRefs } from "pinia";
import { useUserStore } from "@/stores/user";
import { useSnackbarStore } from "@/stores/snackbar";
import { useHead } from "@unhead/vue";
import { useDisplay } from "vuetify";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

useHead({ title: "Home" });

const currentRoute = useRoute();
const user = useUserStore();
const snackbar = useSnackbarStore();
const { lgAndUp } = useDisplay();
const { id: userId, tutorials, loginable } = storeToRefs(user);

const cases = ref([]);
const eApps = ref([]);
const tasks = ref([]);
const advisors = ref([]);
const goals = ref([]);
const statistics = ref([]);
const loadedGoals = ref(false);
const loadedStatistics = ref(false);
const loadedRecents = ref(false);
const goalsAndWebinarsTabIndex = ref(0);

const hideCommissions = computed(() => loginable.value.hide_commission);
const loaded = computed(() => {
  return loadedGoals.value && loadedStatistics.value && loadedRecents.value;
});

const incompleteTaskCount = computed(() => {
  const total = goals.value.length;
  const totalCompleted = goals.value.filter(v => v.completed).length;
  return total - totalCompleted;
});

const percent = computed(() => {
  const total = goals.value.length;
  const totalCompleted = goals.value.filter(v => v.completed).length;
  const percent = totalCompleted / total;
  return Math.trunc(percent * 100);
});

const readyForTutorial = computed(() => {
  return (
    userId.value &&
    loaded.value &&
    !tutorials.value?.home &&
    currentRoute.name === "Home" &&
    lgAndUp.value &&
    loginable.value.type === "Agent"
  );
});

async function fetchAdvisorCards() {
  try {
    statistics.value = await getAdvisorCards();
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loadedStatistics.value = true;
  }
}

async function fetchAdvisorGoals() {
  try {
    goals.value = await getAdvisorGoals();
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loadedGoals.value = true;
  }
}

async function fetchAdvisorRecents() {
  try {
    const res = await getAdvisorRecents();
    cases.value = res.cases;
    tasks.value = res.tasks;
    advisors.value = res.advisors;
    eApps.value = res.eApps;
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loadedRecents.value = true;
  }
}

fetchAdvisorGoals();
fetchAdvisorRecents();
fetchAdvisorCards();
</script>

<style lang="scss">
.recent-item-list {
  .v-card-title {
    padding-top: 16px;
  }
  .v-list-item-title {
    font-size: 1.1rem;
  }
  .v-list-item-subtitle {
    font-size: 1rem;
    padding-bottom: 2px;
  }
}
</style>
